import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FiX, FiZap, FiCopy, FiCheck, FiRefreshCw, FiChevronRight, FiCommand } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { refineContent } from '../../../services/gemini';

const AIPopover = ({
    initialContent,
    onInsert,
    onClose,
    targetName = "contenido", // "título", "descripción", etc.
    position = { top: 0, left: 0 }
}) => {
    const [generatedContent, setGeneratedContent] = useState('');
    const [prompt, setPrompt] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isCopied, setIsCopied] = useState(false);
    const [options, setOptions] = useState(null); // Para mostrar múltiples opciones si se desea
    const popoverRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        // Auto-generación inicial si hay contenido
        if (initialContent && !generatedContent && !isLoading) {
            handleGenerate("Mejóralo para que sea más profesional y claro.");
        }

        // Focus en input al abrir
        if (inputRef.current) {
            inputRef.current.focus();
        }

        const handleClickOutside = (event) => {
            if (popoverRef.current && !popoverRef.current.contains(event.target)) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleGenerate = async (customInstruction) => {
        if (!initialContent) return;
        setIsLoading(true);
        setGeneratedContent(''); // Limpiar para efecto visual
        try {
            // Si ya tenemos contenido generado y el usuario pide un refinamiento, usamos el generado como base?
            // O siempre usamos el initialContent como base? 
            // Lo lógico es refinar lo que ya se ve si el usuario dice "hazlo más corto", 
            // pero por simplicidad y robustez, usaremos initialContent + instrucción acumulada o nueva.
            // Para este MVP, mandamos initialContent + instrucción.

            const result = await refineContent(initialContent, customInstruction);
            setGeneratedContent(result);
        } catch (error) {
            console.error(error);
            toast.error('Error al generar contenido');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(generatedContent);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
        toast.success('Copiado al portapapeles');
    };

    const quickActions = [
        { label: "Más formal", prompt: "Reescríbelo con un tono más formal y ejecutivo." },
        { label: "Más amigable", prompt: "Reescríbelo con un tono cercano, amigable y empático." },
        { label: "Resumir", prompt: "Hazlo más conciso y directo, resumiendo los puntos clave." },
        { label: "Corregir", prompt: "Corrige solo la gramática y ortografía, sin cambiar mucho el estilo." },
    ];


    return ReactDOM.createPortal(
        <div
            ref={popoverRef}
            className="fixed z-[9999] w-[450px] bg-white rounded-xl shadow-2xl border border-indigo-100 flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200"
            style={{
                top: Math.min(position.top, window.innerHeight - 500), // Evitar que se salga por abajo
                left: Math.min(position.left, window.innerWidth - 460) // Evitar que se salga por derecha
            }}
        >
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-50 to-white px-4 py-3 border-b border-indigo-50 flex items-center justify-between">
                <div className="flex items-center gap-2 text-indigo-700 font-semibold">
                    <FiZap className={isLoading ? "animate-pulse" : ""} />
                    <span>Asistente IA</span>
                </div>
                <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                    <FiX size={18} />
                </button>
            </div>

            {/* Content Area */}
            <div className="p-4 bg-gray-50/30 min-h-[150px] max-h-[300px] overflow-y-auto">
                {isLoading ? (
                    <div className="space-y-3 py-4">
                        <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse"></div>
                        <div className="h-4 bg-gray-200 rounded w-full animate-pulse"></div>
                        <div className="h-4 bg-gray-200 rounded w-5/6 animate-pulse"></div>
                    </div>
                ) : (
                    <div className="prose prose-sm max-w-none text-gray-700 leading-relaxed whitespace-pre-wrap animate-in fade-in">
                        {generatedContent || <span className="text-gray-400 italic">Generando sugerencias...</span>}
                    </div>
                )}
            </div>

            {/* Input Area */}
            <div className="p-4 border-t border-gray-100 space-y-3 bg-white">

                {/* Quick Actions */}
                <div className="flex flex-wrap gap-2">
                    {quickActions.map((action, idx) => (
                        <button
                            key={idx}
                            onClick={() => {
                                setPrompt(action.prompt); // Opcional: mostrar lo que se pidió
                                handleGenerate(action.prompt);
                            }}
                            className="px-3 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full hover:bg-indigo-50 hover:text-indigo-600 transition-colors border border-transparent hover:border-indigo-100"
                        >
                            {action.label}
                        </button>
                    ))}
                </div>

                {/* Prompt Input */}
                <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                        <FiCommand size={14} />
                    </div>
                    <input
                        ref={inputRef}
                        type="text"
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !isLoading) {
                                e.preventDefault();
                                handleGenerate(prompt);
                            }
                        }}
                        placeholder={`Instruye a la IA (ej: "hazlo más corto")...`}
                        className="w-full pl-9 pr-10 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all shadow-sm"
                        disabled={isLoading}
                    />
                    <button
                        onClick={() => handleGenerate(prompt)}
                        disabled={!prompt.trim() || isLoading}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-indigo-500 hover:text-indigo-700 disabled:opacity-30 p-1 bg-white rounded-md shadow-sm"
                    >
                        <FiChevronRight size={18} />
                    </button>
                </div>

                {/* Footer Actions */}
                <div className="flex items-center justify-between pt-2">
                    <div className="flex gap-2">
                        <button
                            onClick={handleCopy}
                            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Copiar texto"
                        >
                            {isCopied ? <FiCheck size={18} className="text-green-500" /> : <FiCopy size={18} />}
                        </button>
                        <button
                            onClick={() => handleGenerate(prompt || "Mejóralo otra vez")}
                            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Regenerar"
                        >
                            <FiRefreshCw size={18} className={isLoading ? "animate-spin" : ""} />
                        </button>
                    </div>

                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium transition-colors"
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={() => {
                                if (onInsert && generatedContent) {
                                    onInsert(generatedContent);
                                    onClose();
                                }
                            }}
                            disabled={!generatedContent || isLoading}
                            className="px-5 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium shadow-md shadow-indigo-100 transition-all transform active:scale-95 disabled:opacity-50 disabled:shadow-none"
                        >
                            Insertar
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default AIPopover;
